<template>
  <div class="comparison-tree-table-mobile" :class="[isAllowExpand ? 'table-mobile-disabled-expand' : '']">
    <div class="comparison-tree-table-mobile__header">
      <zm-row class="comparison-tree-table-mobile__header-selection-row">
        <zm-col
          v-for="(col, colIdx) in colList"
          :key="colIdx"
          :class="`comparison-tree-table-mobile__header-${colIdx === 0 ? 'left' : 'right'}-col`"
          class="comparison-tree-table-mobile__header-selection-row__col"
          :span="colSpan"
        >
          <div class="selection-area-wrap">
            <div
              class="selection-area"
              :class="`selection-area--${col.selectOptions.length > 1 ? 'is-select' : 'is-plain-text'}`"
            >
              <zm-select
                v-if="col.selectOptions.length > 1"
                v-model="selectedValues[colIdx]"
                :data-testid="`common-select-plan-comparison-table-col-${colIdx + 1}`"
                placeholder="Please Select"
                :label="`switch types for row ${colIdx + 1}`"
                list-label="Type List"
                popper-append-to-body
              >
                <zm-option
                  v-for="opt in col.selectOptions"
                  :key="opt.value"
                  :label="opt.label"
                  :value="opt.value"
                  :data-testid="`common-option-plan-comparison-table-col-${colIdx + 1}-${opt.value}`"
                />
              </zm-select>
              <div v-else>{{ col.selectOptions[0].label }}</div>
            </div>
          </div>
        </zm-col>
      </zm-row>
      <zm-row v-if="$scopedSlots.price" class="comparison-tree-table-mobile__header-price-row">
        <zm-col
          v-for="(col, colIdx) in colList"
          :key="colIdx"
          :class="`comparison-tree-table-mobile__header-${colIdx === 0 ? 'left' : 'right'}-col`"
          :span="colSpan"
        >
          <div class="price-area">
            <slot name="price" :col="col.selectedConfig" />
          </div>
        </zm-col>
      </zm-row>
      <zm-row v-if="$scopedSlots.button" class="comparison-tree-table-mobile__header-btn-row">
        <zm-col
          v-for="(col, colIdx) in colList"
          :key="colIdx"
          :class="`comparison-tree-table-mobile__header-${colIdx === 0 ? 'left' : 'right'}-col`"
          :span="colSpan"
        >
          <div class="button-area">
            <slot name="button" :col="col.selectedConfig" />
          </div>
        </zm-col>
      </zm-row>
    </div>
    <div class="comparison-tree-table-mobile__inside">
      <zm-collapse v-model="expandRowKeys">
        <template v-for="panel in data">
          <zm-collapse-item v-if="panel.subTitle" :key="`${panel.id}-collapse`" :title="panel.feature" :name="panel.id">
            <PlanRow
              v-for="row in panel.children"
              :key="row.feature"
              :row="row"
              :selectedValues="selectedValues"
              :getColConfigIdx="getColConfigIdx"
              :colSpan="colSpan"
            />
          </zm-collapse-item>
          <PlanRow
            v-else
            :key="`${panel.id}-row`"
            :row="panel"
            :selectedValues="selectedValues"
            :getColConfigIdx="getColConfigIdx"
            :colSpan="colSpan"
          />
        </template>
      </zm-collapse>
    </div>
    <div v-if="!hideCloseComparisonChart && expandRowKeys.length > 0" class="comparison-new-fixed-mobile">
      <div data-testid="common-div-30" class="text" @click="closeTable">
        {{ t('closeComparisonChart') }}
        <i class="zm-icon-up"></i>
      </div>
    </div>
  </div>
</template>

<script>
import PlanRow from './plan-row.vue';
import { t } from '@/plugins/i18n';
import { WorkplaceComparison } from '@/datas/workspace-comparison';
import { fetchBasicConfig } from '@/apis'
import Cookies from 'js-cookie'
import { getCurrencySymbol } from '../ADDON_LEARN_MORE_DIALOG/money.js'

export default {
  name: 'ComparisonTableForMobile',

  components: {
    PlanRow,
  },

  props: {
    appName: {
      type: String,
      default: '',
    },
    comparisonProps: {
      type: Object,
      default: ()=> {}
    },
    treeData: {
      type: Array,
      default: () => [],
    },
    colConfig: {
      type: Array,
      default: () => [],
    },
    isAllowExpand: {
      type: Boolean,
      default: false,
    },
    defaultExpandRowKeys: {
      type: Array,
      default: () => [],
    },
    hideCloseComparisonChart: {
      type: Boolean,
      default: false,
    }
  },

  data() {
    return {
      data: [],
      selectedValues: [],
      expandRowKeys: this.defaultExpandRowKeys,
    };
  },

  computed: {
    colCount() {
      return Math.min(this.colConfig.length, 2);
    },
    colSpan() {
      return this.colCount < 2 ? 24 : 12;
    },
    colList({ colConfig, selectedValues }) {
      return Array.from({ length: this.colCount }, (_, idx) => {
        const disabledValues = selectedValues.filter((_, i) => i !== idx);
        const selectOptions = colConfig
          .filter((it) => !disabledValues.includes(it.key))
          .map((it) => ({ label: it.title, value: it.key }));
        const selectedValue = selectedValues[idx];
        const selectedConfig = colConfig.find((it) => it.key === selectedValue);
        return { selectOptions, selectedValue, selectedConfig };
      });
    },
  },

  watch: {
    colCount: {
      immediate: true,
      handler(colCount) {
        const { colConfig } = this;
        // select the default selected values in sequence from colConfig
        const selectedValues = Array.from({ length: colCount }, (_, idx) => colConfig[idx].key);
        // selectedValues contains the selectedConfig by default
        const selectedConfigIdx = colConfig.findIndex((it) => it.isSelected);
        if (selectedConfigIdx > -1) {
          const selectedConfigKey = colConfig[selectedConfigIdx].key;
          if (!selectedValues.includes(selectedConfigKey)) {
            selectedValues.splice(colCount - 1, 1, selectedConfigKey);
          }
        }
        this.selectedValues = selectedValues;
      },
    },
    appName: {
      // init data
      immediate: true,
      handler(val) {
        // some other products use mobile and do not change this time
        // so still need load data from props
        if (val) {
          this.data = new WorkplaceComparison(this.comparisonProps).build().resultGetter
        } else {
          this.data = this.treeData
        }
        if (this.isAllowExpand && this.data) {
          this.expandRowKeys = [this.data[0].id];
        }
      }
    }
  },
  methods: {
    t,
    getColConfigIdx(colKey) {
      return this.colConfig.findIndex((it) => it.key === colKey);
    },
    closeTable() {
      this.expandRowKeys = [];
    },
    fetchBasicConfig () {
      if (!this.appName) return;
      fetchBasicConfig().then(res => {
        if (!res?.exchangeRates) return;
        const { exchangeRates } = res;
        const _zm_currency = Cookies.get('_zm_currency') || 'USD'
        const currentDollars = exchangeRates.find(e => e.currency === _zm_currency);
        if (!currentDollars) return;
        const { priceForTenDollars, priceForTwoHundredDollars } = currentDollars;
        const currentCurrencyValue = {
          symbol: getCurrencySymbol(_zm_currency),
          priceForTenDollars,
          priceForTwoHundredDollars,
          _zm_currency
        }
        this.data = new WorkplaceComparison({
            ...this.comparisonProps,
            currentCurrencyValue
          }).build().resultGetter
      });
    }
  },
  created () {
    this.fetchBasicConfig();
  },
};
</script>

<style lang="scss">
.comparison-tree-table-mobile {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  height: 100%;

  &__header {
    flex-shrink: 0;
    padding-bottom: 12px;
    width: 100%;
    &-selection-row {
      display: flex;
      align-items: stretch;
      &__col {
        display: flex;
        align-items: stretch;
      }
      .selection-area {
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: center;
        border-bottom: 1px solid #e0e0e6;
        &-wrap {
          padding-left: 8px;
          padding-right: 4px;
          width: 100%;
          display: flex;
          align-items: flex-end;
        }
        &--is-select {
          height: 42px;
        }
        &--is-plain-text {
          line-height: 1.2;
          text-align: center;
          font-size: 16px;
        }
      }
    }
    &-price-row {
      .price-area {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    &-btn-row {
      display: flex;
      align-items: center;
      .button-area {
        display: flex;
        align-items: center;
        justify-content: center;
      }
    }
    .zm-select {
      width: 100%;
      border: 0 none transparent;
      border-radius: 0;
      .zm-select-input {
        padding: 5px 25px 5px 12px;
        .zm-select__caret {
          color: #0b5cff;
        }
      }
      .zm-select-span__inner {
        height: 24px;
        font-size: 16px;
        line-height: 1.2;
        color: #0b5cff;
      }
    }
    &-left-col {
      box-sizing: border-box;
      padding-right: 4px;
    }
    &-right-col {
      box-sizing: border-box;
      padding-left: 4px;
    }
  }

  &__inside {
    flex-grow: 1;
    width: 100%;
    overflow-y: auto;
    border-top: 1px solid #e0e0e6;
    .zm-collapse {
      border: none;
    }
    .zm-collapse-item {
      &.is-active {
        border-bottom: 1px solid #e0e0e6;
        &:last-child {
          border-bottom: none;
        }
      }
      &:last-child {
        .zm-collapse-item__header {
          border-bottom: 0 none transparent;
          &.is-active {
            border-bottom: 1px solid #e0e0e6;
          }
        }
      }
      &__wrap {
        border: none;
      }
      &__content {
        padding-bottom: 0;
      }
      &__header {
        display: flex;
        flex-direction: row-reverse;
        justify-content: flex-end;
        padding: 16px 8px;
        font-size: 14px;
        font-weight: 600;
        line-height: 1.2;
        color: #0b5cff;
        border-bottom: 1px solid #e0e0e6;
        &.is-active {
          border-bottom: 1px solid #e0e0e6;
        }
      }
      &__arrow {
        color: #0b5cff;
        &.zm-icon-right {
          transform: rotate(90deg);
          &.is-active {
            transform: rotate(-90deg);
          }
        }
      }
    }
  }

  .comparison-new-fixed-mobile {
    flex-shrink: 0;
    padding: 16px 0;
    width: 100%;
    font-size: 14px;
    font-weight: 500;
    line-height: 1.5;
    color: #0b5cff;
    .text {
      margin: auto;
      width: fit-content;
      cursor: pointer;
    }
  }

  &.table-mobile-disabled-expand {
    .zm-collapse-item__header {
      pointer-events: none;
    }
    .zm-collapse-item__arrow {
      display: none;
    }
  }
}
</style>
